import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { Search, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";

// Fix for leaflet default marker icon
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconUrl: markerIcon,
  iconRetinaUrl: markerIcon2x,
  shadowUrl: markerShadow,
});

const DEFAULT_CENTER = [22.7196, 75.8577]; // Indore

/** Extract city and locality from Nominatim address object */
function extractCityLocality(addr) {
  const city =
    addr?.city || addr?.town || addr?.village || addr?.county || addr?.state || "";
  const locality =
    addr?.suburb ||
    addr?.neighbourhood ||
    addr?.residential ||
    addr?.quarter ||
    addr?.road ||
    "";
  return { city, locality };
}

/**
 * MapPicker — interactive Leaflet map with:
 *  - Debounced autocomplete suggestions (Nominatim)
 *  - Click-on-map + drag-pin to set position
 *  - Reverse geocoding on every position change
 *  - onChange emits: { lat, lng, address, city, locality }
 */
export default function MapPicker({ value, onChange }) {
  const [position, setPosition] = useState(
    value?.lat && value?.lng ? [value.lat, value.lng] : DEFAULT_CENTER
  );
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingGeo, setLoadingGeo] = useState(false);
  const debounceRef = useRef(null);
  const markerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Sync external value → internal position
  useEffect(() => {
    if (value?.lat && value?.lng) {
      setPosition([value.lat, value.lng]);
    }
  }, [value?.lat, value?.lng]);

  // Close suggestions on outside click
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Reverse-geocode lat/lng → address + locality + city
  const updatePosition = useCallback(
    async (lat, lng) => {
      setPosition([lat, lng]);
      setLoadingGeo(true);
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
        );
        if (res.ok) {
          const data = await res.json();
          const address = data.display_name || "";
          const { city, locality } = extractCityLocality(data.address);
          onChange({ lat, lng, address, city, locality });
        } else {
          onChange({ lat, lng, address: "", city: "", locality: "" });
        }
      } catch (e) {
        console.error(e);
        onChange({ lat, lng, address: "", city: "", locality: "" });
      } finally {
        setLoadingGeo(false);
      }
    },
    [onChange]
  );

  // Map click handler (inner component so it can use useMapEvents)
  function MapEvents() {
    useMapEvents({
      click(e) {
        const { lat, lng } = e.latlng;
        updatePosition(lat, lng);
      },
    });
    return null;
  }

  // Draggable marker
  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker) {
          const { lat, lng } = marker.getLatLng();
          updatePosition(lat, lng);
        }
      },
    }),
    [updatePosition]
  );

  // Debounced autocomplete: fire Nominatim search 400 ms after user stops typing
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!searchQuery.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
            searchQuery
          )}&addressdetails=1&limit=6`
        );
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data || []);
          setShowSuggestions(true);
        }
      } catch (e) {
        console.error(e);
      }
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchQuery]);

  // User picks a suggestion
  const handleSelectSuggestion = (item) => {
    const lat = parseFloat(item.lat);
    const lng = parseFloat(item.lon);
    const address = item.display_name || "";
    const { city, locality } = extractCityLocality(item.address);
    setPosition([lat, lng]);
    setSearchQuery(item.display_name || "");
    setSuggestions([]);
    setShowSuggestions(false);
    onChange({ lat, lng, address, city, locality });
  };

  return (
    <div className="relative w-full rounded-2xl overflow-hidden border border-stone-200 bg-stone-50">
      {/* Search + suggestions — float above the map (z-[1000]) */}
      <div ref={wrapperRef} className="absolute top-3 left-3 right-3 z-[1000]">
        {/* Input row */}
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-3.5 text-stone-400 pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
            placeholder="Search a location, city or address…"
            className="pl-9 pr-9 h-11 bg-white/95 border-stone-200 shadow-md backdrop-blur-sm rounded-xl focus-visible:ring-brand text-stone-900"
          />
          {/* Spinner while reverse-geocoding */}
          {loadingGeo && (
            <Loader2 className="w-4 h-4 absolute right-3 top-3.5 text-stone-400 animate-spin" />
          )}
          {/* Clear button */}
          {searchQuery && !loadingGeo && (
            <button
              type="button"
              onClick={() => {
                setSearchQuery("");
                setSuggestions([]);
                setShowSuggestions(false);
              }}
              className="absolute right-3 top-3.5 text-stone-400 hover:text-stone-700"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Autocomplete suggestions dropdown */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="mt-1 bg-white border border-stone-200 rounded-xl shadow-lg overflow-hidden">
            {suggestions.map((item, i) => (
              <button
                key={i}
                type="button"
                onMouseDown={() => handleSelectSuggestion(item)}
                className="w-full text-left px-4 py-2.5 text-sm text-stone-700 hover:bg-stone-50 border-b border-stone-100 last:border-0 flex gap-3 items-start"
              >
                <Search className="w-3.5 h-3.5 mt-0.5 shrink-0 text-stone-400" />
                <span className="line-clamp-1">{item.display_name}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Map */}
      <div className="h-[320px] w-full">
        <MapContainer
          center={position}
          zoom={13}
          scrollWheelZoom={true}
          style={{ height: "100%", width: "100%" }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={position}
            draggable={true}
            eventHandlers={eventHandlers}
            ref={markerRef}
          />
          <MapEvents />
          <MapController position={position} />
        </MapContainer>
      </div>
    </div>
  );
}

/** Smoothly pan the map whenever the controlled position changes */
function MapController({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, map.getZoom(), { animate: true });
    }
  }, [position, map]);
  return null;
}
